import StyledTableCell from "@components/Table/TableCell";
import StyledTableRow from "@components/Table/TableRow";
import { TableRowsSkeleton } from "@components/Table/TableRowsSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import SwipeIcon from "@mui/icons-material/Swipe";
import {
  Box,
  Card,
  CardContent,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useClientModelVariables } from "@services/api";
import { ClientModelVariable, ModelScenarioInput } from "@services/api/types";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

const getTimePeriodVarValue = (
  model_id: string,
  variable: ClientModelVariable,
  timePeriods: string[],
  inputs: ModelScenarioInput[]
): JSX.Element[] => {
  const varInputs = inputs.filter((input) => input.name === variable.name);

  return timePeriods.map((timePeriod) => {
    const value = varInputs.find(
      (input) => input.time_period === timePeriod
    )?.value;

    return (
      <StyledTableCell
        align="center"
        key={`${model_id}-${variable.name}-${timePeriod}`}
      >
        {variable.input_type === "input" && value}
        {variable.input_type === "switch" && (
          <Switch checked={(value || 0) > 0} color="info" />
        )}
      </StyledTableCell>
    );
  });
};

export interface ExternalVariablesProps {
  inputs: ModelScenarioInput[];
  timePeriods: string[];
}

const ExternalVariables = ({ inputs, timePeriods }: ExternalVariablesProps) => {
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconVisibility = isMobile ? "" : "hidden";
  const iconJustify = isMobile ? "justify-left" : "";

  const { data: modelVars, isFetching: isLoading } = useClientModelVariables(
    currentClient.model.id,
    "external"
  );

  const timePeriodHeader = useMemo(() => {
    return timePeriods.map((timePeriod) => (
      <StyledTableCell
        align="center"
        key={`scenario-external-var-${timePeriod}`}
      >
        {timePeriod}
      </StyledTableCell>
    ));
  }, [timePeriods]);

  const tableRows = useMemo(() => {
    return (modelVars || []).map((modelVar) => {
      return (
        <StyledTableRow
          key={`${currentClient.model.id}-${modelVar.name}`}
          sx={{
            "&:last-child td, &:last-child th": { border: 0 }
          }}
        >
          <StyledTableCell align="center">{modelVar.name}</StyledTableCell>
          {getTimePeriodVarValue(
            currentClient.model.id.toString(),
            modelVar,
            timePeriods,
            inputs
          )}
        </StyledTableRow>
      );
    });
  }, [currentClient.model.id, inputs, modelVars, timePeriods]);

  return (
    <Card
      className={`w-full mt-8 ${(modelVars || []).length === 0 ? "hidden" : ""}`}
    >
      <CardContent>
        <Typography variant="h6" component="div">
          {t("simulator.scenario.external_variables")}
        </Typography>
        <Box className={`w-full flex pt-4 ${iconVisibility} ${iconJustify}`}>
          <SwipeIcon
            sx={{
              bottom: 12,
              opacity: 0.4,
              left: "50%",
              transform: "translate(750%, 0%)",
              color: theme.palette.primary.main
            }}
            fontSize="small"
          />
        </Box>
        <TableContainer
          className="w-full p-4 overflow-y-scroll"
          sx={{
            maxHeight: "calc(100vh - 450px)",
            minHeight: 300,
            overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
            scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
            "&::-webkit-scrollbar": {
              display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
            }
          }}
        >
          <Table
            className="w-full"
            aria-label="scenario external vars table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                {inputs.length > 0 && (
                  <StyledTableCell align="center">
                    {t("simulator.scenario.table.external_variables.variable")}
                  </StyledTableCell>
                )}
                {timePeriodHeader}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || inputs.length === 0 ? (
                <TableRowsSkeleton
                  id={`${currentClient.model.id}-external-variables-skeleton`}
                  numberOfRows={5}
                  columns={5}
                />
              ) : (
                tableRows
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
export default ExternalVariables;
